import React from 'react';
import Link from 'next/link';

/**
 * Gallery·Field section heading: the mono "eyebrow" mark (with an optional
 * right-aligned tag) plus an optional h2 title — optionally paired with an
 * "all →" link in a `gf-h2-row`.
 *
 * Extracted so the heading markup lives in one place instead of being
 * copy-pasted into every section. Renders a fragment (mark + h2); any
 * `gf-lead` paragraph and the section body stay in the calling component.
 *
 * Props:
 *  - eyebrow   : mono overline text (the `.no` span)
 *  - tag       : optional right-aligned `.r` chip (e.g. "FAQ", a count)
 *  - title     : optional h2 text; omit for mark-only sections
 *  - allHref   : optional href for the trailing "all →" link; falsy → no link
 *                (lets callers keep conditional links, e.g. allHref={x ? '…' : null})
 *  - allLabel  : label for the "all →" link; its presence renders the gf-h2-row
 *  - allExternal: render the link as a plain <a> instead of next/link
 *  - actions   : optional extra node(s) rendered on the right of the gf-h2-row
 *                (e.g. a group of `.gf-alllink` links); also forces the row
 */
export default function GfSectionMark({
    eyebrow,
    tag = null,
    title = null,
    allHref = null,
    allLabel = null,
    allExternal = false,
    actions = null,
}) {
    const allLink = allHref && allLabel
        ? (allExternal
            ? <a className="gf-alllink" href={allHref}>{allLabel}</a>
            : <Link className="gf-alllink" href={allHref}>{allLabel}</Link>)
        : null;

    const right = (allLink || actions)
        ? <div className="gf-h2-actions">{allLink}{actions}</div>
        : null;

    return (
        <>
            <div className="gf-mark">
                <span className="no">{eyebrow}</span>
                {tag ? <span className="r">{tag}</span> : null}
            </div>
            {title != null && (
                right
                    ? <div className="gf-h2-row"><h2 className="gf-h2">{title}</h2>{right}</div>
                    : <h2 className="gf-h2">{title}</h2>
            )}
        </>
    );
}
